import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageFileUpload from "./ImageFileUpload";
import {getInputFocusStyle, toYearMonthDay} from "../pages/utils";
import SymptomSeverities from "./SymptomSeverities";
import SymptomImages from "./SymptomImages";
import InitContext from "../pages/InitContext";
import {faXmark} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

const Symptom = ({ symptomParams, category = null, onUpdateSymptom = null, onDeleteImage = null,
                   onAddImages, onSaveNewSymptoms = null}) => {
  const { t } = useTranslation();
  const todayYmd = useMemo(() => toYearMonthDay(new Date()), []);
  const [symptoms, setSymptoms] = useState([]);
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);
  const context = useContext(InitContext);

  const getReportingUrl = () => {
    const langMap = {
      de: "https://nebenwirkungen.bund.de/SiteGlobals/Forms/nebenwirkungen/01-person/person-node.html",
      sl: "https://www.jazmp.si/humana-zdravila/farmakovigilanca/porocanje-o-nezelenih-ucinkih-zdravil/",
      nl: "https://meldformulier.lareb.nl/Forms/reportform",
      el: "https://www.eof.gr/web/guest",
      en: "https://www.adrreports.eu/en/report_side_effect.html",
    };

    return langMap[context?.language] || langMap.en;
  };

  useEffect(() => {
    if (Array.isArray(symptomParams) && symptomParams.length > 0) {
      const initSymptoms = symptomParams.map((param, i) => ({
        id: param?.id ?? -1,
        type: param?.type ?? category ?? "",
        term: param?.term ?? "",
        sufferingLevel: typeof param?.sufferingLevel === "number" ? param.sufferingLevel : undefined,
        comment: param?.comment ?? "",
        number: i,
        startDate: param?.startDate ? toYearMonthDay(param.startDate) : todayYmd,
        endDate: param?.endDate ? toYearMonthDay(param.endDate) : "",
        images: param?.images ?? [],
      }));

      setSymptoms(initSymptoms);
    } else {
      setSymptoms([]);
    }
  }, [symptomParams, category, todayYmd]);

  const handleSelectedSufferingLevel = (sufferingLevel, ind) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) => (i === ind ? { ...symptom, sufferingLevel } : symptom))
    );
  };

  const handleStartDates = (value, ind) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) => (i === ind ? { ...symptom, startDate: value } : symptom))
    );
  };

  const handleEndDates = (value, ind) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) =>
        i === ind ? { ...symptom, endDate: value || "", deactivated: !!value } : symptom
      )
    );
  };

  const updateComments = (value, ind) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) => (i === ind ? { ...symptom, comment: value } : symptom))
    );
  };

  const handleFilesChanged = (symptomNumber, files) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) => (i === symptomNumber ? { ...symptom, files } : symptom))
    );
  };

  const handleSubmit = (cancel) => {
    const payload = cancel === true ? null : symptoms;

    if (category === null || !onSaveNewSymptoms) {
      onUpdateSymptom?.(payload);
    } else {
      onSaveNewSymptoms?.(payload);
    }
  };

  const checkRequiredFields = () =>
    symptoms.length > 0 && symptoms.every((symptom) => typeof symptom?.sufferingLevel === "number");

  return (
    <>
      <label className="symptom-edit-subtitle">{t("please_select_the_severity_of_the_symptoms")}</label>
      <div className="w3-container form-content-scroll symptom-edit-surface">
        {symptoms.map((symptom, i) => (
          <div key={`symptom-${i}`} className="w3-margin symptom-edit-card">
            <header className="feedback-modal-header">
              <h4>{t(symptom?.term)}</h4>
              <FontAwesomeIcon
                icon={faXmark}
                className="modal-close"
                onClick={() => handleSubmit(true)}
              />
            </header>
            <div className="w3-container symptom-edit-card-body">
              <SymptomSeverities
                category={category}
                symptom={symptom}
                symptomNumber={i}
                handleSelectedSufferingLevel={handleSelectedSufferingLevel}
              />
              <div className="w3-row w3-padding-small symptom-edit-form-row">
                <div className="w3-quarter symptom-edit-field symptom-edit-field--date">
                  <label>{t("start_date")}*</label>
                  <input
                    type="date"
                    value={symptom?.startDate || todayYmd}
                    className="w3-input w3-border w3-round form-input symptom-edit-input"
                    onChange={(e) => handleStartDates(e.target.value, i)}
                  />
                </div>
                <div className="w3-quarter symptom-edit-field symptom-edit-field--date">
                  <label>{t("end_date")}</label>
                  <input
                    type="date"
                    value={symptom?.endDate || ""}
                    className="w3-input w3-border w3-round form-input symptom-edit-input"
                    onChange={(e) => handleEndDates(e.target.value, i)}
                  />
                </div>
                <div className="w3-half symptom-edit-field symptom-edit-field--comment">
                  <label>{t("comment")}:</label>
                  <textarea
                    className="form-input symptom-edit-input symptom-edit-textarea"
                    value={symptom?.comment || ""}
                    onFocus={() => setIsTextFieldFocused(true)}
                    onBlur={() => setIsTextFieldFocused(false)}
                    onChange={(e) => updateComments(e.target.value, i)}
                    style={getInputFocusStyle(isTextFieldFocused)}
                  />
                </div>
              </div>
              <div className="w3-row w3-padding-small symptom-edit-media-row">
                <div className="w3-col s7 w3-mobile">
                { category === null
                  ?
                  <SymptomImages symptom={symptom} onDeleteImage={onDeleteImage} onAddImages={onAddImages} />
                  :
                  <ImageFileUpload symptomNumber={i} onFilesChanged={handleFilesChanged} />
                }
                </div>
                <div className="w3-col s5 w3-mobile w3-padding-small w3-right-align symptom-edit-report-link">
                  <a href={getReportingUrl()} target="_blank" rel="noopener noreferrer">
                    <label className="label-link">{t("report_to_national_authorities")}</label>
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <footer className="w3-container symptom-edit-footer">
        <div className="w3-right symptom-edit-footer-actions w3-margin-right">
          <button
            onClick={() => handleSubmit(false)}
            type="button"
            className="symptom-ui-button symptom-ui-button--solid-primary symptom-edit-submit-button"
            disabled={!checkRequiredFields()}
          >
            {t("submit")}
          </button>
        </div>
      </footer>
    </>
  );
};

export default Symptom;
